import { getOutlineControlProps } from 'utils/buttonUtils';
import { supportsTariffExclusions } from '@billing/validation/rules';
import { ScopeMode } from '@billing/validation/rules';
import {
  ActionIcon,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  useComputedColorScheme,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import {
  getClaimCriteriaField,
  getClaimCriteriaOperators,
  isWhenRangeOperator,
  getBillingHistoryPeriod,
  getBillingHistoryPeriodUpdate,
} from './ruleUtils';
import {
  billingHistoryPeriodOptions,
  historyCriteriaFields,
  historyCriteriaScopeModes,
} from './constants';
import { whenOperatorSchema } from './ruleSchemas';
import type { WhenClause } from './ruleSchemas';
import type { RuleBuilder } from './useRuleBuilder';
import { RuleCriteriaValueFields } from './RuleCriteriaValueFields';
import { RuleServiceCriteriaFields } from './RuleServiceCriteriaFields';

export interface RuleCriteriaClauseProps {
  builder: RuleBuilder;
  clause: WhenClause;
  index: number;
  joinerKey?: string;
  canRemove?: boolean;
}

const RuleCriteriaScopeSelect = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const { scopeModeOptions, updateWhenClause } = builder;
  const claimField = getClaimCriteriaField(clause.scopeMode);

  const handleScopeChange = (value: string | null): void => {
    const scopeMode = scopeModeOptions.find(
      (option) => option.value === value
    )?.value;
    if (!scopeMode) return;
    updateWhenClause(clause.id, {
      scopeMode,
      whenOperator:
        historyCriteriaFields.find((field) => field.scopeMode === scopeMode)
          ?.operator ?? 'is',
      ...(getClaimCriteriaField(scopeMode) ||
      claimField ||
      historyCriteriaScopeModes.has(scopeMode) ||
      historyCriteriaScopeModes.has(clause.scopeMode)
        ? { scopeValuesText: '' }
        : {}),
    });
  };

  return (
    <Select
      data={scopeModeOptions}
      value={clause.scopeMode}
      onChange={handleScopeChange}
      allowDeselect={false}
      w={historyCriteriaScopeModes.has(clause.scopeMode) ? '100%' : 260}
      data-testid={
        index === 0
          ? 'rule-scope-mode-select'
          : `rule-scope-mode-select-${index}`
      }
    />
  );
};

const RuleBillingHistoryPeriodSelect = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const { translate, updateWhenClause } = builder;
  const period = getBillingHistoryPeriod(clause.billingHistory);
  const handlePeriodChange = (value: string | null): void => {
    if (!value) return;
    updateWhenClause(clause.id, {
      billingHistory: {
        ...clause.billingHistory,
        ...getBillingHistoryPeriodUpdate(value),
      },
    });
  };

  return (
    <Select
      aria-label={translate('pages:rules.createModal.billingHistory.window')}
      data={[
        ...billingHistoryPeriodOptions.map((option) => ({
          value: option.value,
          label: translate(`pages:rules.historyCriteria.${option.labelKey}`),
        })),
        ...(period === 'rolling'
          ? [
              {
                value: 'rolling',
                label: translate(
                  'pages:rules.createModal.billingHistory.rolling'
                ),
              },
            ]
          : []),
      ]}
      value={period}
      onChange={handlePeriodChange}
      allowDeselect={false}
      w="100%"
      data-testid={`rule-billing-history-window-${index}`}
    />
  );
};

const RuleDuplicateClaimOperatorSelect = ({
  builder,
  index,
}: RuleCriteriaClauseProps): ReactElement => (
  <Select
    data={[
      {
        value: 'exists',
        label: builder.translate('pages:rules.editWhenOperators.exists'),
      },
    ]}
    value="exists"
    allowDeselect={false}
    w="100%"
    data-testid={
      index === 0
        ? 'rule-when-operator-select'
        : `rule-when-operator-select-${index}`
    }
  />
);

const RuleComparisonOperatorSelect = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const { translate, updateWhenClause, localizedWhenOperatorOptions } = builder;
  const claimField = getClaimCriteriaField(clause.scopeMode);
  const handleOperatorChange = (value: string | null): void => {
    const parsed = whenOperatorSchema.safeParse(value || 'is');
    if (!parsed.success) return;
    const whenOperator = parsed.data;
    const wasRange = isWhenRangeOperator(clause.whenOperator);
    const nextIsRange = isWhenRangeOperator(whenOperator);
    updateWhenClause(clause.id, {
      whenOperator,
      ...(wasRange && !nextIsRange
        ? { scopeValuesText: clause.scopeValuesText.split(',')[0] ?? '' }
        : {}),
    });
  };

  return (
    <Select
      data={
        claimField
          ? getClaimCriteriaOperators(clause.scopeMode).map((value) => ({
              value,
              label: translate(`pages:rules.editWhenOperators.${value}`),
            }))
          : localizedWhenOperatorOptions
      }
      value={clause.whenOperator}
      onChange={handleOperatorChange}
      allowDeselect={false}
      w={150}
      data-testid={
        index === 0
          ? 'rule-when-operator-select'
          : `rule-when-operator-select-${index}`
      }
    />
  );
};

const RuleCriteriaOperatorSelect = (
  props: RuleCriteriaClauseProps
): ReactElement | null => {
  switch (props.clause.scopeMode) {
    case ScopeMode.TariffBillingHistory:
      return <RuleBillingHistoryPeriodSelect {...props} />;
    case ScopeMode.DuplicateClaim:
      return <RuleDuplicateClaimOperatorSelect {...props} />;
    case ScopeMode.PatientAge:
    case ScopeMode.ServiceStartTimeLocal:
    case ScopeMode.ServiceStartDayLocal:
      return null;
    case ScopeMode.AnyClaim:
      if (props.clause.whenOperator === 'is') return null;
      return <RuleComparisonOperatorSelect {...props} />;
    default:
      return <RuleComparisonOperatorSelect {...props} />;
  }
};

const RuleBillingHistoryOptions = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement | null => {
  if (clause.scopeMode !== ScopeMode.TariffBillingHistory) return null;
  const { translate, updateWhenClause } = builder;
  const history = clause.billingHistory;
  return (
    <Stack gap="xs" ml={{ base: 0, sm: 48 }} pl={{ base: 0, sm: 'sm' }}>
      <Text size="xs" c="dimmed">
        {translate('pages:rules.historyCriteria.tariffHint')}
      </Text>
      {getBillingHistoryPeriod(history) === 'rolling' && (
        <Group>
          <NumberInput
            label={translate('pages:rules.createModal.billingHistory.amount')}
            min={1}
            allowDecimal={false}
            allowNegative={false}
            value={history.amount}
            onChange={(value) =>
              updateWhenClause(clause.id, {
                billingHistory: { ...history, amount: String(value) },
              })
            }
            data-testid={`rule-billing-history-amount-${index}`}
          />
          <Select
            label={translate('pages:rules.createModal.billingHistory.unit')}
            data={(['days', 'weeks', 'months'] as const).map((unit) => ({
              value: unit,
              label: translate(
                `pages:rules.createModal.billingHistory.${unit}`
              ),
            }))}
            value={history.unit}
            onChange={(value) => {
              if (value !== 'days' && value !== 'weeks' && value !== 'months')
                return;
              updateWhenClause(clause.id, {
                billingHistory: { ...history, unit: value },
              });
            }}
            allowDeselect={false}
            data-testid={`rule-billing-history-unit-${index}`}
          />
        </Group>
      )}
      <Checkbox
        label={translate('pages:rules.createModal.billingHistory.sameProvider')}
        checked={history.sameProvider}
        onChange={(event) =>
          updateWhenClause(clause.id, {
            billingHistory: {
              ...history,
              sameProvider: event.currentTarget.checked,
            },
          })
        }
        data-testid={`rule-billing-history-same-provider-${index}`}
      />
    </Stack>
  );
};

/** Keeps clause identity, row layout, and removal behavior independent of value editors. */
export const RuleCriteriaRow = (
  props: RuleCriteriaClauseProps
): ReactElement => {
  const colorScheme = useComputedColorScheme();
  const {
    builder,
    clause,
    index,
    joinerKey = 'pages:rules.editControls.when',
    canRemove = builder.whenClauses.length > 1,
  } = props;
  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      bg={colorScheme === 'dark' ? 'dark.6' : 'white'}
    >
      <Stack gap="xs">
        <Group
          align="center"
          gap="sm"
          wrap="wrap"
          display={
            historyCriteriaScopeModes.has(clause.scopeMode)
              ? { base: 'flex', sm: 'grid' }
              : undefined
          }
          w="100%"
          style={
            historyCriteriaScopeModes.has(clause.scopeMode)
              ? {
                  gridTemplateColumns:
                    '48px minmax(160px, 1fr) minmax(150px, 0.85fr) minmax(180px, 1.2fr) 28px',
                }
              : undefined
          }
        >
          <Text c="dimmed" size="sm">
            {builder.translate(joinerKey)}
          </Text>
          <RuleCriteriaScopeSelect {...props} />
          <RuleCriteriaOperatorSelect {...props} />
          <RuleCriteriaValueFields {...props} />
          <ActionIcon
            {...getOutlineControlProps(colorScheme, !canRemove)}
            color={colorScheme === 'dark' ? 'white' : 'dark'}
            onClick={() => builder.removeWhenClause(clause.id)}
            disabled={!canRemove}
            aria-label={`${builder.translate(
              'pages:rules.editControls.removeScope'
            )} ${index + 1}`}
            data-testid={`rule-remove-when-${index}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
        {supportsTariffExclusions(clause.scopeMode, clause.whenOperator) && (
          <TextInput
            label={builder.translate('pages:rules.groups.exceptTariffs')}
            description={builder.translate(
              'pages:rules.groups.exceptTariffsHint'
            )}
            value={clause.excludedTariffValuesText}
            onChange={(event) =>
              builder.updateWhenClause(clause.id, {
                excludedTariffValuesText: event.currentTarget.value,
              })
            }
            data-testid={`rule-excluded-tariffs-${index}`}
          />
        )}
        <RuleServiceCriteriaFields {...props} />
        <RuleBillingHistoryOptions {...props} />
      </Stack>
    </Paper>
  );
};
